/**
 * JobDetail Page
 * Detailed view of a specific job posting
 * MOBILE RESPONSIVE VERSION - REDESIGNED
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users, 
  Calendar,
  Briefcase,
  Building,
  Globe,
  Share2,
  Bookmark,
  BookmarkCheck,
  CheckCircle,
  AlertCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Info,
  Award,
  Target
} from 'lucide-react';
import ApplicationModal from '../components/ApplicationModal';
import { getJobById, hasUserApplied } from '../services/careerService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * JobDetail Page Component
 * 
 * @returns {JSX.Element} JobDetail component
 */
const JobDetail = () => {
  const { jobId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    requirements: false,
    benefits: false,
    company: false
  });

  // Load job details
  useEffect(() => {
    const loadJobDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const jobData = await getJobById(jobId);
        if (!jobData) {
          setError('Job not found');
          return;
        }

        setJob(jobData);

        // Check if user has applied
        if (currentUser) {
          const applied = await hasUserApplied(currentUser.uid, jobId);
          setHasApplied(applied);
        }
      } catch (err) {
        console.error('Error loading job details:', err);
        setError(err.message || 'Failed to load job details');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      loadJobDetails();
    }
  }, [jobId, currentUser]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle job application
  const handleApply = () => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    
    if (hasApplied) {
      toast.info('You have already applied to this job');
      return;
    }
    
    setShowApplicationModal(true);
  };

  // Handle successful application
  const handleApplicationSuccess = () => {
    setHasApplied(true);
    setShowApplicationModal(false);
    toast.success('Application submitted successfully!');
  };

  // Handle save job
  const handleSaveJob = () => {
    setIsSaved(!isSaved);
    toast.success(isSaved ? 'Job removed from saved' : 'Job saved for later');
  };

  // Handle share job
  const handleShareJob = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.title,
          text: `Check out this ${job.title} position at ${job.company.name}`,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Job link copied to clipboard');
    }
  };

  // Format salary range
  const formatSalary = (min, max, currency = 'ZAR') => {
    if (!min && !max) return 'Not specified';
    if (min && max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    return min ? `From ${currency} ${min.toLocaleString()}` : `Up to ${currency} ${max.toLocaleString()}`;
  };

  // Format posted date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get job type badge color
  const getJobTypeBadge = (type) => {
    const colors = {
      'full-time': 'bg-green-100 text-green-800',
      'part-time': 'bg-blue-100 text-blue-800',
      'contract': 'bg-purple-100 text-purple-800',
      'internship': 'bg-orange-100 text-orange-800',
      'remote': 'bg-indigo-100 text-indigo-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Get experience level color
  const getExperienceColor = (level) => {
    const colors = {
      'entry-level': 'text-green-600',
      'mid-level': 'text-blue-600',
      'senior-level': 'text-purple-600',
      'executive': 'text-red-600'
    };
    return colors[level] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Job Not Found</h3>
          <p className="text-gray-600 mb-6">{error || 'The job you\'re looking for doesn\'t exist.'}</p>
          <Link
            to="/career-opportunities"
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Browse Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <Link
              to="/career-opportunities"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Back to Jobs</span>
              <span className="sm:hidden">Back</span>
            </Link>

            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveJob}
                className="p-2 text-gray-600 hover:text-yellow-600 transition-colors rounded-full hover:bg-gray-100"
                title={isSaved ? 'Remove from saved' : 'Save job'}
              >
                {isSaved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
              </button>
              
              <button
                onClick={handleShareJob}
                className="p-2 text-gray-600 hover:text-green-600 transition-colors rounded-full hover:bg-gray-100"
                title="Share job"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Job Header Card */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          {/* Company Logo and Basic Info */}
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 border">
              {job.company.logo ? (
                <img 
                  src={job.company.logo} 
                  alt={job.company.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-xl">
                  {job.company.name.charAt(0)}
                </div>
              )}
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                <div className="min-w-0 flex-1 mb-3 sm:mb-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 leading-tight">
                    {job.title}
                  </h1>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                    <div className="flex items-center text-gray-600">
                      <Building className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="font-medium">{job.company.name}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>{job.location}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getJobTypeBadge(job.jobType)}`}>
                    {job.jobType.charAt(0).toUpperCase() + job.jobType.slice(1).replace('-', ' ')}
                  </span>
                  {job.isUrgent && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      Urgent
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Key Job Info Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Salary</p>
              <p className="font-semibold text-sm sm:text-base text-gray-900">
                {formatSalary(job.salary?.min, job.salary?.max, job.salary?.currency)}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Briefcase className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Experience</p>
              <p className={`font-semibold text-sm sm:text-base ${getExperienceColor(job.experienceLevel)}`}>
                {job.experienceLevel.charAt(0).toUpperCase() + job.experienceLevel.slice(1).replace('-', ' ')}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Applicants</p>
              <p className="font-semibold text-sm sm:text-base text-gray-900">
                {job.applicationCount?.length || 0}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Calendar className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Posted</p>
              <p className="font-semibold text-sm sm:text-base text-gray-900">
                {formatDate(job.createdAt)}
              </p>
            </div>
          </div>

          {/* Apply Button and Urgency */}
          <div className="text-center">
            {hasApplied ? (
              <div className="inline-flex items-center bg-green-50 text-green-800 px-6 py-3 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Application Submitted</span>
              </div>
            ) : (
              <button
                onClick={handleApply}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-sm hover:shadow-md text-lg"
              >
                Apply for This Job
              </button>
            )}

            {job.isUrgent && !hasApplied && (
              <div className="mt-3 flex items-center justify-center text-red-600 text-sm">
                <Clock className="w-4 h-4 mr-1" />
                <span className="font-medium">Urgent hiring - Apply quickly!</span>
              </div>
            )}

            {job.applicationDeadline && (
              <div className="mt-2 text-sm text-gray-500">
                Application deadline: {formatDate(job.applicationDeadline)}
              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Description */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <button
                onClick={() => toggleSection('description')}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Info className="w-5 h-5 text-blue-500 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Job Description</h2>
                </div>
                {expandedSections.description ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.description && (
                <div className="px-6 pb-6">
                  <div className="prose max-w-none text-gray-700">
                    <p className="whitespace-pre-line leading-relaxed">{job.description}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <button
                  onClick={() => toggleSection('requirements')}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-purple-500 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Requirements</h2>
                  </div>
                  {expandedSections.requirements ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.requirements && (
                  <div className="px-6 pb-6 space-y-4">
                    {job.requirements.skills && job.requirements.skills.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Required Skills</h3>
                        <div className="flex flex-wrap gap-2">
                          {job.requirements.skills.map((skill, index) => (
                            <span 
                              key={index}
                              className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {job.requirements.education && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Education</h3>
                        <p className="text-gray-700 leading-relaxed">{job.requirements.education}</p>
                      </div>
                    )}
                    
                    {job.requirements.experience && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Experience</h3>
                        <p className="text-gray-700 leading-relaxed">{job.requirements.experience}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <button
                  onClick={() => toggleSection('benefits')}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Award className="w-5 h-5 text-green-500 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Benefits & Perks</h2>
                  </div>
                  {expandedSections.benefits ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSections.benefits && (
                  <div className="px-6 pb-6">
                    <div className="grid gap-3">
                      {job.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Company Information */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <button
                onClick={() => toggleSection('company')}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors lg:cursor-default lg:hover:bg-white"
                disabled={window.innerWidth >= 1024}
              >
                <div className="flex items-center">
                  <Building className="w-5 h-5 text-blue-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">About {job.company.name}</h3>
                </div>
                <div className="lg:hidden">
                  {expandedSections.company ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
              
              <div className={`px-6 pb-6 ${expandedSections.company || window.innerWidth >= 1024 ? 'block' : 'hidden'}`}>
                {job.company.description && (
                  <p className="text-gray-700 mb-4 leading-relaxed">{job.company.description}</p>
                )}
                
                <div className="space-y-3">
                  {job.company.industry && (
                    <div className="flex items-center text-sm">
                      <Briefcase className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">Industry:</span>
                      <span className="font-medium ml-2">{job.company.industry}</span>
                    </div>
                  )}
                  
                  {job.company.size && (
                    <div className="flex items-center text-sm">
                      <Users className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">Company Size:</span>
                      <span className="font-medium ml-2">{job.company.size}</span>
                    </div>
                  )}
                  
                  {job.company.website && (
                    <div className="flex items-center text-sm">
                      <Globe className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                      <a 
                        href={job.company.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 font-medium transition-colors"
                      >
                        Visit Company Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Summary */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Job Type:</span>
                  <span className="font-medium">{job.jobType.replace('-', ' ')}</span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Experience:</span>
                  <span className="font-medium">{job.experienceLevel.replace('-', ' ')}</span>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{job.location}</span>
                </div>
                
                {job.remote && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Remote:</span>
                    <span className="font-medium text-green-600">Available</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Posted:</span>
                  <span className="font-medium">{formatDate(job.createdAt)}</span>
                </div>
                
                {job.applicationDeadline && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Deadline:</span>
                    <span className="font-medium text-red-600">{formatDate(job.applicationDeadline)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Call to Action */}
            {!hasApplied && (
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-2">Ready to Apply?</h3>
                <p className="text-green-100 text-sm mb-4">
                  Join {job.company.name} and take the next step in your career.
                </p>
                <button
                  onClick={handleApply}
                  className="w-full bg-white text-green-600 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Apply Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && (
        <ApplicationModal
          job={job}
          onClose={() => setShowApplicationModal(false)}
          onSuccess={handleApplicationSuccess}
        />
      )}
    </div>
  );
};

export default JobDetail;